(function () {
  'use strict';

  const { iman, subEvents, PUB_SUB_EVENTS } = window.Universe;


  class ShippingBarElement {
    constructor(element) {
      if (!element) return;
      this.DOM = { element: element };

      this.threshold = 0;
      this.showIcon = false;
      this.shippingMessage = '';
      this.successMessage = '';
      this.currencySymbol = '$';
      this.currencyIsoCode = 'USD';
      this.lastKnownCartTotal = null;

      this.handleCartUpdate = this.handleCartUpdate.bind(this);

      this.cacheDOM();
      this.initializeAttributes();
      this.bindEvents();

      // For non-base currencies the Liquid-rendered values are based on the
      // unconverted base threshold, so re-render once with the converted value.
      if (this.currencyRate !== 1) {
        this.updateDisplay();
      }
    }

    destroy() {
      this.unbindEvents();
    }

    cacheDOM() {
      const cartDrawer = document.querySelector('.js-cart-drawer-wrap');
      if (cartDrawer) {
        this.currencySymbol = cartDrawer.dataset.currencySymbol || '$';
        this.currencyIsoCode = cartDrawer.dataset.currencyIsoCode || 'USD';
      }

      this.DOM.progressFill = this.DOM.element.querySelector('.shipping-bar__progress-fill');
      this.DOM.truckIcon = this.DOM.element.querySelector('.shipping-bar__truck-icon');
      this.DOM.message = this.DOM.element.querySelector('.shipping-bar__text');
    }

    initializeAttributes() {
      this.threshold = parseInt(this.DOM.element.dataset.threshold, 10) || 0;
      this.showIcon = this.DOM.element.dataset.showIcon === 'true';
      this.shippingMessage = this.DOM.element.dataset.shippingMessage;
      this.successMessage = this.DOM.element.dataset.successMessage;

      // Seed the cart total from the server-rendered value so the initial
      // re-render (for converted currencies) is accurate before any cart event.
      const initialCartTotal = parseInt(this.DOM.element.dataset.cartTotal, 10);
      if (!Number.isNaN(initialCartTotal)) {
        this.lastKnownCartTotal = initialCartTotal;
      }

      // Convert the base-currency threshold to the active presentment currency
      // using the rate Shopify exposes, so merchants only enter the value once.
      this.currencyRate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
      this.threshold = Math.round(this.threshold * this.currencyRate);
    }

    bindEvents() {
      this.cartUpdateUnsubscriber = subEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      this.onTemplateUpdated = (e) => {
        if (e.detail?.isEmpty) this.DOM.element.classList.add('shipping-bar--hidden');
      };
      document.addEventListener('cart-drawer:template-updated', this.onTemplateUpdated);
    }

    unbindEvents() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      document.removeEventListener('cart-drawer:template-updated', this.onTemplateUpdated);
    }

    handleCartUpdate(data) {
      if (data && data.cartData && data.cartData.total_price !== undefined) {
        this.lastKnownCartTotal = data.cartData.total_price;
        this.updateDisplay();
      } else {
        this.fetchAndUpdate();
      }
    }

    fetchAndUpdate() {
      fetch('/cart.json')
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Failed to fetch cart data');
        })
        .then((cartData) => {
          this.lastKnownCartTotal = cartData.total_price || 0;
          this.updateDisplay();
        })
        .catch((error) => {
          console.warn('ShippingBar: Error fetching cart data, using fallback', error);
          this.useFallbackAndUpdate();
        });
    }

    useFallbackAndUpdate() {
      if (window.cart && window.cart.total_price !== undefined) {
        this.lastKnownCartTotal = window.cart.total_price;
        this.updateDisplay();
        return;
      }

      if (this.lastKnownCartTotal !== null) {
        this.updateDisplay();
        return;
      }
    }

    getCurrentCartTotal() {
      if (this.lastKnownCartTotal !== null) {
        return this.lastKnownCartTotal;
      }

      if (window.cart && window.cart.total_price !== undefined) {
        return window.cart.total_price;
      }

      return 0;
    }

    formatMoney(cents) {
      const amount = cents / 100;

      if (this.currencyIsoCode === 'EUR') {
        return '\u20AC' + amount.toFixed(2);
      }
      if (this.currencyIsoCode === 'GBP') {
        return '\u00A3' + amount.toFixed(2);
      }
      return this.currencySymbol + amount.toFixed(2);
    }

    updateDisplay() {
      const cartTotal = this.getCurrentCartTotal();
      const remainingAmount = Math.max(0, this.threshold - cartTotal);
      const progressPercentage = Math.min(100, (cartTotal / this.threshold) * 100);
      const hasReachedThreshold = cartTotal >= this.threshold;

      if (this.DOM.progressFill) {
        this.DOM.progressFill.style.width = progressPercentage + '%';
      }

      if (this.DOM.truckIcon && this.showIcon) {
        this.DOM.truckIcon.style.display = 'flex';
        if (hasReachedThreshold) {
          this.DOM.truckIcon.classList.add('shipping-bar__truck-icon--reached');
        } else {
          this.DOM.truckIcon.classList.remove('shipping-bar__truck-icon--reached');
        }
      } else if (this.DOM.truckIcon) {
        this.DOM.truckIcon.style.display = 'none';
      }

      if (this.DOM.message) {
        if (hasReachedThreshold) {
          this.DOM.message.textContent = this.successMessage;
          this.DOM.message.classList.add('shipping-bar__text--success');
          this.DOM.element.classList.add('shipping-bar--completed');
        } else {
          const remainingFormatted = this.formatMoney(remainingAmount);
          const message = this.shippingMessage.replace('[amount]', remainingFormatted);
          this.DOM.message.textContent = message;
          this.DOM.message.classList.remove('shipping-bar__text--success');
          this.DOM.element.classList.remove('shipping-bar--completed');
        }
      }

      if (cartTotal !== 0) {
        this.DOM.element.classList.remove('shipping-bar--hidden');
      }

      this.DOM.element.dispatchEvent(
        new CustomEvent('shipping-bar:updated', {
          detail: {
            cartTotal: cartTotal,
            remainingAmount: remainingAmount,
            progressPercentage: progressPercentage,
            hasReachedThreshold: hasReachedThreshold,
          },
          bubbles: true,
        })
      );
    }
  }


  class ShippingBar {
    constructor() {
      this.init();
    }

    init(container = document) {
      const elements = container.querySelectorAll('.js-shipping-bar');
      if (elements.length > 0) {
        this.DOM = {};
        this.DOM.elements = elements;
        this.instances = [];
        this.setup();
      }
    }

    setup() {
      this.instances = Array.from(this.DOM.elements).map((element) => {
        return new ShippingBarElement(element);
      });
    }

    reinit() {
      this.destroy();
      this.init();
    }

    destroy() {
      if (this.instances) {
        this.instances.forEach((instance) => { instance.destroy(); });
        this.instances = null;
        this.DOM = null;
      }
    }
  }


  function init() {
    if (!iman.get('shippingBar')) {
      iman.add('shippingBar', new ShippingBar());
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Universe.ShippingBar = ShippingBar;
})();
